import { FieldParameter } from "@fartherfinance/frontend/api/TaskWorkflows/Types";
import { getValidationRules } from "@fartherfinance/frontend/api/TaskWorkflows/utils/fieldHelpers";
import { hasDataType } from "@fartherfinance/frontend/api/TaskWorkflows/utils/guards";

import {
  ExternalResources,
  ValidateFn,
  ValidationRules,
} from "../../core/types";
import { resolveExpressions } from "../../expressions/resolveExpressions";

import { templateFieldValidator } from "./templateFieldValidator";

const reportError = (component: string, error: unknown) => {
  console.error(`[FormEngine:${component}]`, error);
};

const processValidationResults = (
  validationResult: unknown[]
): string | true => {
  if (validationResult.some((result) => result !== true)) {
    const errorMessages = validationResult
      .filter((result) => result !== true)
      .map((result) => (typeof result === "string" ? result : "Invalid value"));
    return errorMessages.join(" ");
  }
  return true;
};

const createValidationFunction = (
  apiValidationRules: Array<{ type: string; errorMessage?: string }>,
  fp: FieldParameter,
  getValues: (key: string) => unknown,
  externalResources?: ExternalResources
): ValidateFn => {
  return async (value) => {
    try {
      const validator = templateFieldValidator(
        value,
        getValues,
        externalResources
      );

      // Process API validation rules
      const apiValidationResults = await Promise.all(
        apiValidationRules.map(async (rule) => {
          if (rule.errorMessage) {
            // Skip complex validators like regex that require params when they only have errorMessage
            // These should be handled by the dataType.validation instead
            if (rule.type === "regex") {
              // Skip API-level regex rules, rely on dataType validation for proper regex validation
              return [true];
            }
            return await validator({
              function: rule.type,
              errorMessage: rule.errorMessage,
            });
          } else {
            return [true];
          }
        })
      );

      // Also process dataType validation if it exists (this ensures complete validation coverage)
      const dataTypeValidationResults =
        hasDataType(fp) && fp.dataType.validation
          ? await Promise.all(
              fp.dataType.validation.map((validationExpr) =>
                validator(validationExpr)
              )
            )
          : [true];

      // Combine both validation results
      const combinedValidationResult = [
        ...apiValidationResults.flat(),
        ...dataTypeValidationResults,
      ];

      return processValidationResults(combinedValidationResult);
    } catch (error) {
      reportError("FieldValidator", error);
      return "Validation error occurred.";
    }
  };
};

const createLegacyValidationFunction = (
  fp: FieldParameter,
  getValues: (key: string) => unknown,
  externalResources?: ExternalResources
): ValidateFn => {
  return async (value) => {
    try {
      const validator = templateFieldValidator(
        value,
        getValues,
        externalResources
      );

      const validationResults = await Promise.all(
        fp.dataType!.validation!.map((validationExpr) =>
          validator(validationExpr)
        )
      );

      return processValidationResults(validationResults);
    } catch (error) {
      reportError("FieldValidator", error);
      return "Validation error occurred.";
    }
  };
};

export const buildRules = async (
  fp: FieldParameter,
  getValues: (key: string) => unknown,
  externalResources?: ExternalResources
): Promise<ValidationRules> => {
  const rules: ValidationRules = {
    required: false,
  };

  try {
    // Use API utility to get validation rules
    const apiValidationRules = getValidationRules(fp);

    // Handle required field validation
    if (fp.display && "required" in fp.display && fp.display.required) {
      if (typeof fp.display.required === "boolean" && fp.display.required) {
        rules.required = "This field is required.";
      } else if (Array.isArray(fp.display.required)) {
        try {
          // Check if all the required expressions evaluate to true
          const isRequired = await resolveExpressions(
            fp.display.required,
            getValues,
            externalResources
          );

          // The expression is required if all results are truthy, typically all true
          if (isRequired && isRequired.every((result) => Boolean(result))) {
            rules.required = "This field is required.";
          } else {
            rules.required = false;
          }
        } catch (error) {
          // On error, default to not required (assume falsy)
          rules.required = false;
          console.warn("Error resolving required expressions:", error);
        }
      }
    }

    // Use API validation rules - consolidate validation logic
    if (apiValidationRules.length > 0) {
      rules.validate = createValidationFunction(
        apiValidationRules,
        fp,
        getValues,
        externalResources
      );
    } else if (hasDataType(fp) && fp.dataType.validation?.length) {
      // Fallback to legacy validation for compatibility
      rules.validate = createLegacyValidationFunction(
        fp,
        getValues,
        externalResources
      );
    }
  } catch (error) {
    reportError("BuildRules", error);
  }

  return rules;
};
